import React from 'react';
import { motion } from 'motion/react';
import { 
  User, 
  Shield, 
  Palette, 
  Bell, 
  CreditCard, 
  Settings as SettingsIcon, 
  Monitor, 
  Globe, 
  Zap,
  LogOut,
  Building2,
  ChevronDown
} from 'lucide-react';

interface SettingsSidebarProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  onLogout?: () => void;
  onOrganizationSelect?: () => void;
}

const settingsTabs = [
  { id: 'security', label: 'Security', icon: Shield },
  { id: 'appearance', label: 'Appearance', icon: Palette },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'energy', label: 'Energy Settings', icon: Zap },
  { id: 'system', label: 'System', icon: Monitor },
  { id: 'integrations', label: 'Integrations', icon: Globe },
  { id: 'billing', label: 'Billing', icon: CreditCard },
];

const SettingsSidebar: React.FC<SettingsSidebarProps> = ({ 
  activeTab, 
  onTabChange, 
  onLogout,
  onOrganizationSelect,
}) => {
  return (
    <div className="w-64 rounded-2xl border border-gray-100/50 bg-white/80 p-4 shadow-lg backdrop-blur-sm">
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <div className="rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 p-2">
            <SettingsIcon className="h-5 w-5 text-white" />
          </div>
          <h2 className="text-lg font-bold text-gray-900">Settings</h2>
        </div>
        <p className="mt-1 text-sm text-gray-600">Manage your account and preferences</p>
      </div>

      <nav className="space-y-1">
        {settingsTabs.map((tab, index) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;

          return (
            <motion.button
              key={tab.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05, duration: 0.3 }}
              onClick={() => onTabChange(tab.id)}
              className={`flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left transition-all duration-200 ${
                isActive
                  ? 'border border-blue-100 bg-blue-50 text-blue-700 shadow-sm'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <Icon className={`h-5 w-5 ${isActive ? 'text-blue-600' : 'text-gray-500'}`} />
              <span className="font-medium">{tab.label}</span>
              {isActive && <motion.div layoutId="activeTab" className="ml-auto h-2 w-2 rounded-full bg-blue-600" />}
            </motion.button>
          );
        })}
      </nav>

      <div className="mt-8 space-y-4">
        {/* Pro Plan Info */}
        <div className="rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 p-4">
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-emerald-500 p-1">
              <Zap className="h-4 w-4 text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-emerald-800">Pro Plan</p>
              <p className="text-xs text-emerald-600">All features unlocked</p>
            </div>
          </div>
        </div>

        {/* Organization Select */}
        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.3 }}
          onClick={onOrganizationSelect}
          className="flex w-full items-center gap-3 rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-3 text-left transition-all duration-200 hover:border-gray-300 hover:bg-gray-100/50 group"
        >
          <div className="rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 p-1.5 group-hover:from-indigo-600 group-hover:to-purple-700 transition-all">
            <Building2 className="h-4 w-4 text-white" />
          </div>
          <div className="flex-1">
            <p className="font-medium text-[14px] text-gray-900">Change Organization</p>
            <p className="text-xs text-gray-600">Switch organization</p>
          </div>
        </motion.button>

        {/* Logout Button */}
        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.3 }}
          onClick={onLogout}
          className="flex w-full items-center gap-3 rounded-xl border border-red-100 bg-red-50/50 px-4 py-3 text-left transition-all duration-200 hover:border-red-200 hover:bg-red-100/70 group"
        >
          <div className="rounded-lg  bg-red-100 p-1.5 group-hover:bg-red-200 transition-colors">
            <LogOut className="h-4 w-4 text-red-600" />
          </div>
          <div className="flex-1">
            <p className="font-medium text-[14px] text-red-700">Logout</p>
            <p className="text-xs text-red-600">Sign out of your account</p>
          </div>
        </motion.button>
      </div>
    </div>
  );
};

export default SettingsSidebar;